import React from 'react';
import { useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Colors, Text } from 'library';
import theme from 'theme';

import type { SubPoolSummary, SubPoolInfoCard } from '../types';
import { getElcIcon } from './elcIcons';
import {
    CardWrapper,
    StyledAccordion,
    StyledAccordionSummary,
    StyledAccordionDetails,
    SummaryContent,
    Logo,
    TitleBlock,
    InfoCardGrid,
    InfoCard,
    InfoCardLink,
    IconCircle,
    ExternalLink,
} from './elcStyles';

function InfoCardContent({ card }: { card: SubPoolInfoCard }) {
    const Icon = getElcIcon(card.icon);
    return (
        <>
            <IconCircle>{Icon ? <Icon style={{ color: Colors.white, fontSize: 22 }} /> : null}</IconCircle>
            <Text variant="body1" bold textStyle={{ paddingBottom: 4 }}>
                {card.title}
                {card.link && <OpenInNewIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} />}
            </Text>
            <Text variant="body2" color={Colors.mediumGrey}>
                {card.description}
            </Text>
        </>
    );
}

export function ELCResourceCard({ partner }: { partner: SubPoolSummary }) {
    const medium = useMediaQuery(theme.breakpoints.down('md'));
    const small = useMediaQuery(theme.breakpoints.down('sm'));

    const gridColumns = small ? '1fr' : medium ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)';

    return (
        <CardWrapper>
            <StyledAccordion defaultExpanded={false} elevation={0} disableGutters>
                <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <SummaryContent>
                        <Logo
                            src={partner.logo_url || ''}
                            alt={partner.full_name ?? partner.name}
                            width={partner.logo_width_card ?? undefined}
                        />
                        <TitleBlock>
                            <Text variant="h1" bold>
                                {`${partner.name}'s Substitute Pool`}
                            </Text>
                            <Text variant="body2" color={Colors.mediumGrey}>
                                Powered by Tandem — Resources &amp; Info
                            </Text>
                        </TitleBlock>
                    </SummaryContent>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                    <Text variant="body1" textStyle={{ paddingBottom: 16 }}>
                        {partner.description}
                    </Text>
                    <InfoCardGrid style={{ gridTemplateColumns: gridColumns }}>
                        {partner.info_cards.map((card) =>
                            card.link ? (
                                <InfoCardLink
                                    key={card.title}
                                    href={card.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <InfoCardContent card={card} />
                                </InfoCardLink>
                            ) : (
                                <InfoCard key={card.title}>
                                    <InfoCardContent card={card} />
                                </InfoCard>
                            ),
                        )}
                    </InfoCardGrid>
                    {partner.website_url && (
                        <ExternalLink href={partner.website_url} target="_blank" rel="noopener noreferrer">
                            <IconCircle>
                                <OpenInNewIcon style={{ color: Colors.white, fontSize: 22 }} />
                            </IconCircle>
                            <div>
                                <Text variant="body1" bold>
                                    {`${partner.name} Website`}{' '}
                                    <OpenInNewIcon style={{ fontSize: 14, verticalAlign: 'middle' }} />
                                </Text>
                                <Text variant="body2" color={Colors.mediumGrey}>
                                    {`Explore resources, programs, and support from the ${partner.name}.`}
                                </Text>
                            </div>
                        </ExternalLink>
                    )}
                </StyledAccordionDetails>
            </StyledAccordion>
        </CardWrapper>
    );
}
